/**
 * In-pane fallback for the Asset Library — used when the Office Dialog API
 * isn't available (browser dev, tests, or a host that doesn't support
 * displayDialogAsync). Renders AssetLibraryContent behind a `<dialog>`
 * backdrop that fills the task pane, same overlay approach as
 * command-palette/components/CommandPalette.tsx.
 *
 * When the Dialog API IS available, DeckorateModule opens
 * AssetLibraryDialogApp in a real Office Dialog window instead (see
 * hooks/openAssetLibraryDialog.ts) — that floats over the whole PowerPoint
 * window rather than being squeezed into the narrow task pane.
 */

import { useEffect, useRef } from "react";
import { AssetLibraryContent } from "./AssetLibraryContent";
import styles from "./AssetLibraryDialog.module.css";

interface AssetLibraryDialogProps {
  readonly onClose: () => void;
}

export function AssetLibraryDialog({ onClose }: Readonly<AssetLibraryDialogProps>) {
  const panelRef = useRef<HTMLDivElement>(null);

  // Backdrop click-to-close — same approach as CommandPalette.tsx (a mouse
  // handler on the role="dialog" overlay itself trips the "non-native
  // interactive element" lint rule).
  useEffect(() => {
    function handleOutsideMouseDown(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    }
    document.addEventListener("mousedown", handleOutsideMouseDown);
    return () => document.removeEventListener("mousedown", handleOutsideMouseDown);
  }, [onClose]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return (
    <dialog open className={styles.overlay} aria-modal="true" aria-label="Asset Library">
      <div ref={panelRef} className={styles.panel} data-testid="asset-library-panel">
        <AssetLibraryContent onClose={onClose} />
      </div>
    </dialog>
  );
}
